import {
  Dispatch,
  MouseEvent,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import { SearchByInputType } from 'src/graphql';

export type CaseStatusTab = 'open' | 'closed';

interface UseCaseStatusTabParams {
  caseQueryParamFields: SearchByInputType[] | null;
  setCaseQueryParamFields: Dispatch<SetStateAction<SearchByInputType[] | null>>;
  activeCard: string | null;
  setActiveCard: Dispatch<SetStateAction<string | null>>;
  setPage: (page: number) => void;
}

export const useCaseStatusTab = ({
  caseQueryParamFields,
  setCaseQueryParamFields,
  activeCard,
  setActiveCard,
  setPage,
}: UseCaseStatusTabParams) => {
  const [caseStatusTab, setCaseStatusTab] = useState<CaseStatusTab>('open');

  // An explicit status search (deep-linked ?caseStatus=... or the legacy
  // dropdown) stays on the restricted searchBy path; tab/card status goes
  // through appliedFilters.caseStatus instead — see getCasesAndCount.ts.
  const hasExplicitStatusSearch = (caseQueryParamFields ?? []).some(
    (field) => field.fieldName === 'Case.status',
  );
  const caseStatusFilter = hasExplicitStatusSearch
    ? undefined
    : activeCard === 'waitingForResponse'
      ? 'new'
      : caseStatusTab === 'closed'
        ? 'closed'
        : '!closed';

  // Syncs the toggle with a deep-linked ?caseStatus=closed URL.
  useEffect(() => {
    const statusField = (caseQueryParamFields ?? []).find(
      (field) => field.fieldName === 'Case.status',
    );
    if (!statusField) return;
    setCaseStatusTab(statusField.fieldValue === 'closed' ? 'closed' : 'open');
  }, [caseQueryParamFields]);

  const handleCaseStatusTabChange = (
    _event: MouseEvent<HTMLElement>,
    nextTab: CaseStatusTab | null,
  ) => {
    if (!nextTab || nextTab === caseStatusTab) return;

    if (activeCard === 'waitingForResponse') {
      setActiveCard(null);
    }

    setCaseStatusTab(nextTab);
    setPage(1);

    const nonStatusFields = (caseQueryParamFields ?? []).filter(
      (field) => field.fieldName !== 'Case.status',
    );
    setCaseQueryParamFields(
      nonStatusFields.length > 0 ? nonStatusFields : null,
    );
  };

  return {
    caseStatusTab,
    setCaseStatusTab,
    caseStatusFilter,
    handleCaseStatusTabChange,
  };
};
